import { Link, Paragraph, Stack } from '@customerio/pluma-components/react';
import type { CreditsPurchaseTermsLinks } from '../utils/credits-purchase-config';

const FEATURE_TERMS_URL = 'https://customer.io/legal/feature-terms';
const TERMS_OF_SERVICE_URL = 'https://customer.io/legal/terms-of-service';

type CreditsPurchaseDisclaimerProps = {
  /** The credit-type-specific disclaimer line shown above the terms links. */
  disclaimer: string;
  /** The rate the credits are drawn down at, for a rail that quotes one. */
  rateNote?: string;
  /** Which terms the purchase agrees to; defaults to Feature Terms and Terms of Service. */
  termsLinks?: CreditsPurchaseTermsLinks;
};

/**
 * The shared fine print for the credit-purchase forms: a credit-type disclaimer line followed by the
 * terms the purchase agrees to. Each sentence is the legal-approved wording for its rail, so the two
 * variants are spelled out rather than assembled.
 */
export function CreditsPurchaseDisclaimer({
  disclaimer,
  rateNote,
  termsLinks = 'feature-and-service',
}: CreditsPurchaseDisclaimerProps) {
  const termsOfService = (
    <Link href={TERMS_OF_SERVICE_URL} variant="secondary" isExternal={true}>
      Terms of Service
    </Link>
  );

  const isServiceOnly = termsLinks === 'service-only';

  return (
    <Stack mt="200">
      <Paragraph color="secondary" size="xs">
        {disclaimer}
        {rateNote == null ? null : ` ${rateNote}`}
        {isServiceOnly ? <> By completing this purchase you agree to our {termsOfService}.</> : null}
      </Paragraph>
      {!isServiceOnly ? (
        <Paragraph color="secondary" size="xs">
          By completing this purchase, you agree to our{' '}
          <Link href={FEATURE_TERMS_URL} variant="secondary" isExternal={true}>
            Feature Terms
          </Link>{' '}
          and {termsOfService}.
        </Paragraph>
      ) : null}
    </Stack>
  );
}
